import { Link, useLocation, useParams } from 'react-router';
import type { MonitoringCheck, Service } from '@bekebi/contracts/api';
import { SERVICE_TYPE_LABELS, hasServiceHealth } from '@bekebi/domain';
import { ApiError } from '../../api/client';
import { ErrorState, LoadingState } from '../../components/states';
import { useHasPermission } from '../../session/current-user';
import {
  mailboxSummaryText,
  isServiceId,
  useLatestCheckResult,
  useMailboxCapability,
  useService,
  useServiceBindings,
  useServiceChecks,
} from './api';
import {
  DefinitionList,
  HealthSummary,
  LastChecked,
  RenewalSummary,
  Section,
  ServiceTypeIcon,
  UnavailableNote,
} from './components';
import { CHECK_KIND_TITLES, formatDateTime } from './format';
import {
  CHECK_ERROR_ACTIONS,
  CheckStateBadge,
  ResultStatus,
  formatInterval,
  targetSummary,
} from '../monitoring/present';
import { BackupStatusSection } from './backup-status';
import { CloudServerMetrics } from './cloud-metrics';
import { StorageBucketUsage } from './storage-usage';
import { WebsiteAddressForm } from './website-address-form';

/**
 * Service detail (/app/services/:serviceId). Everything shown is read from the API: the service
 * record with its derived health and renewal summary, and — where the Admin's permissions include
 * them — the configured checks and infrastructure bindings. The only change offered is the
 * Website's public address. Data the portal does not have is labelled as not connected.
 */
export function ServiceDetail() {
  const { serviceId } = useParams();
  if (!isServiceId(serviceId)) return <ServiceNotFound />;
  return <ServiceDetailFor id={serviceId} />;
}

function ServiceNotFound() {
  return (
    <div className="max-w-3xl">
      <h1>Service not found</h1>
      <p className="mt-2 text-sm text-muted">There is no service at this address.</p>
      <BackToServices />
    </div>
  );
}

function BackToServices() {
  return (
    <p className="mt-4 text-sm">
      <Link
        to="/app/services"
        className="text-accent-link underline decoration-accent-link/40 underline-offset-4 hover:decoration-accent-link"
      >
        Back to Services
      </Link>
    </p>
  );
}

function ServiceDetailFor({ id }: { id: string }) {
  const service = useService(id);
  if (service.isPending) return <LoadingState label="Loading service…" />;
  if (service.isError) {
    if (service.error instanceof ApiError && service.error.status === 404) {
      return <ServiceNotFound />;
    }
    return (
      <ErrorState
        title="The service could not be loaded"
        message={service.error.message}
        onRetry={() => void service.refetch()}
      />
    );
  }
  return <ServiceView service={service.data} />;
}

function ServiceView({ service }: { service: Service }) {
  const location = useLocation();
  const created = (location.state as { created?: string } | null)?.created;
  const canViewChecks = useHasPermission('monitoring.view');
  const canViewBindings = useHasPermission('infrastructure.view');
  const canViewRenewals = useHasPermission('renewals.view');

  return (
    <div className="max-w-4xl space-y-6">
      <div>
        <p className="text-sm">
          <Link
            to="/app/services"
            className="text-accent-link underline decoration-accent-link/40 underline-offset-4 hover:decoration-accent-link"
          >
            Services
          </Link>
        </p>
        <div className="mt-3 flex items-center gap-4">
          <ServiceTypeIcon type={service.type} size="lg" />
          <div className="min-w-0">
            <h1>{service.name}</h1>
            <p className="mt-1 text-sm text-muted">
              Service type: {SERVICE_TYPE_LABELS[service.type]} · Added{' '}
              {formatDateTime(service.createdAt)}
            </p>
          </div>
        </div>
        {created ? (
          <p role="status" className="mt-4 rounded-md bg-success-soft p-3 text-sm text-success">
            {created} was added.
          </p>
        ) : null}
      </div>

      {/* The Storage Bucket has no health state (director, 2026-11-02): its space and backups below. */}
      {hasServiceHealth(service.type) ? (
        <Section title="Health" description={HEALTH_SCOPE[service.type]}>
          <HealthSummary health={service.health} />
        </Section>
      ) : null}

      {service.type === 'website' ? <WebsiteAddressSection service={service} /> : null}

      {canViewChecks && hasServiceHealth(service.type) ? <ChecksSection service={service} /> : null}

      {service.type === 'cloud_server' ? <CloudServerMetrics serviceId={service.id} /> : null}

      {service.type === 'mail_server' ? <MailboxSection /> : null}
      {service.type === 'mail_server' ? <BackupStatusSection /> : null}

      {service.type === 'storage_bucket' ? <StorageBucketUsage serviceId={service.id} /> : null}
      {service.type === 'storage_bucket' ? <BackupStatusSection /> : null}

      <Section
        title="Renewal"
        action={
          canViewRenewals ? (
            <Link
              to="/app/renewals"
              className="text-sm text-accent-link underline decoration-accent-link/40 underline-offset-4 hover:decoration-accent-link"
            >
              Open Renewals
            </Link>
          ) : null
        }
      >
        <RenewalSummary renewal={service.renewal} />
      </Section>

      {canViewBindings ? <BindingsSection serviceId={service.id} /> : null}
    </div>
  );
}

/** What this service's health is (and is not) based on. */
const HEALTH_SCOPE: Record<Service['type'], string> = {
  cloud_server:
    'Application health: whether the Cloud Server application answers its status check correctly. This is not host or server health.',
  mail_server:
    'Protocol availability: whether the SMTP and IMAP services answer over TLS. This is not proof that mail is delivered.',
  storage_bucket: 'The Storage Bucket has no health state: it is shown by its space and backups.',
  website: 'Whether the public address answers over HTTPS with a successful response.',
};

function WebsiteAddressSection({ service }: { service: Service }) {
  const canManage = useHasPermission('services.manage');
  return (
    <Section
      title="Public address"
      description={
        canManage
          ? "The Website's public address. Changing it clears the Website check's last result; a new result appears when the monitoring worker next checks the new address."
          : "The Website's public address."
      }
    >
      <DefinitionList
        items={[
          [
            'Current address',
            <span key="a" className="break-all font-mono">
              {service.accessUrl ?? 'Not set'}
            </span>,
          ],
        ]}
      />
      {canManage ? (
        <div className="mt-5">
          <WebsiteAddressForm serviceId={service.id} current={service.accessUrl} />
        </div>
      ) : null}
    </Section>
  );
}

function ChecksSection({ service }: { service: Service }) {
  const checks = useServiceChecks(service.id, true);
  return (
    <Section
      title="Checks"
      description={
        service.type === 'mail_server'
          ? 'SMTP and IMAP checks confirm the services answer over TLS with the right greeting. A passing check is not proof that email is delivered.'
          : 'The scheduled checks this service’s health is derived from.'
      }
      action={
        <Link
          to="/app/monitoring"
          className="text-sm text-accent-link underline decoration-accent-link/40 underline-offset-4 hover:decoration-accent-link"
        >
          Open Monitoring
        </Link>
      }
    >
      {checks.isPending ? (
        <LoadingState label="Loading checks…" />
      ) : checks.isError ? (
        <ErrorState
          title="Checks could not be loaded"
          message={checks.error.message}
          onRetry={() => void checks.refetch()}
        />
      ) : checks.data.length === 0 ? (
        <UnavailableNote>No checks are configured for this service.</UnavailableNote>
      ) : (
        <ul className="divide-y divide-line" aria-label="Checks">
          {checks.data.map((check) => (
            <li key={check.id} className="py-3 first:pt-0 last:pb-0">
              <CheckRow check={check} />
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

/**
 * One check with its latest real result: status, time, duration and — when it failed or warned —
 * the probe's fixed message and what to do. Nothing is shown before the first result exists.
 */
function CheckRow({ check }: { check: MonitoringCheck }) {
  const latest = useLatestCheckResult(check.id, check.lastCheckedAt !== null);
  const result = latest.data ?? null;

  return (
    <div className="space-y-1 text-sm">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="font-semibold text-fg">{CHECK_KIND_TITLES[check.kind]}</h3>
        <CheckStateBadge check={check} />
      </div>
      <p className="text-muted">{check.description}</p>
      <p className="break-all font-mono text-xs text-muted">{targetSummary(check)}</p>
      <p className="text-muted">
        Last checked: <LastChecked checkedAt={check.lastCheckedAt} /> ·{' '}
        {formatInterval(check.intervalSeconds)}
      </p>
      {result ? (
        <div className="space-y-1" aria-label="Latest result">
          <p className="flex flex-wrap items-center gap-2 text-muted">
            <span>Latest result:</span>
            <ResultStatus status={result.status} />
            <span>
              {formatDateTime(result.checkedAt)}
              {result.durationMs !== null ? ` · took ${result.durationMs} ms` : ''}
            </span>
          </p>
          {result.errorCode ? (
            <>
              <p className={result.status === 'failed' ? 'text-danger' : 'text-warning'}>
                {result.errorMessage}
              </p>
              <p className="text-fg">What to do: {CHECK_ERROR_ACTIONS[result.errorCode]}</p>
            </>
          ) : null}
        </div>
      ) : latest.isError ? (
        <p className="text-muted">The latest result could not be loaded.</p>
      ) : null}
    </div>
  );
}

function BindingsSection({ serviceId }: { serviceId: string }) {
  const bindings = useServiceBindings(serviceId, true);
  return (
    <Section
      title="Infrastructure bindings"
      description="The infrastructure resources this service runs on."
      action={
        <Link
          to="/app/infrastructure"
          className="text-sm text-accent-link underline decoration-accent-link/40 underline-offset-4 hover:decoration-accent-link"
        >
          Open Infrastructure
        </Link>
      }
    >
      {bindings.isPending ? (
        <LoadingState label="Loading bindings…" />
      ) : bindings.isError ? (
        <ErrorState
          title="Bindings could not be loaded"
          message={bindings.error.message}
          onRetry={() => void bindings.refetch()}
        />
      ) : bindings.data.length === 0 ? (
        <UnavailableNote>No infrastructure resources are bound to this service.</UnavailableNote>
      ) : (
        <ul className="divide-y divide-line" aria-label="Bindings">
          {bindings.data.map((binding) => (
            <li key={binding.id} className="py-3 text-sm first:pt-0 last:pb-0">
              <p className="font-semibold">
                <Link
                  to={`/app/infrastructure/${binding.resource.id}`}
                  className="text-accent-link underline decoration-accent-link/40 underline-offset-4 hover:decoration-accent-link"
                >
                  {binding.resource.name}
                </Link>
              </p>
              <p className="text-muted">
                Kind: {binding.resource.kind} · Role: {binding.role}
                {binding.scopeRef ? ` · Scope: ${binding.scopeRef}` : ''}
              </p>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

function MailboxSection() {
  const canView = useHasPermission('mail.mailboxes.view');
  const status = useMailboxCapability(canView);
  if (canView && status.data?.capability === 'available') {
    return (
      <Section title="Mailboxes">
        <p className="text-sm text-fg-2">
          {mailboxSummaryText(status.data)}{' '}
          <Link
            to="/app/mailboxes"
            className="text-accent-link underline decoration-accent-link/40 underline-offset-4 hover:decoration-accent-link"
          >
            Open Mailboxes
          </Link>
        </p>
      </Section>
    );
  }
  let text = 'Mailbox management is not part of this screen yet.';
  if (status.isPending && canView) text = 'Checking whether mailbox management is connected…';
  else if (status.data) text = mailboxSummaryText(status.data);
  return (
    <Section title="Mailboxes">
      <UnavailableNote>{text}</UnavailableNote>
    </Section>
  );
}
